import axios from 'axios';
import { hasAdminAuthorizationHeader, refreshAdminAccessToken } from './adminAccessToken';

type AuthenticationFailure = 'unauthenticated' | 'forbidden';

const getAuthenticationFailure = (results: readonly PromiseSettledResult<unknown>[]): AuthenticationFailure | null => {
  const statuses = results.flatMap((result) => result.status === 'rejected' && axios.isAxiosError(result.reason)
    ? [result.reason.response?.status]
    : []);
  if (statuses.includes(403)) return 'forbidden';
  if (statuses.includes(401)) return 'unauthenticated';
  return null;
};

export async function requestAdminNotifications<T extends readonly PromiseSettledResult<unknown>[]>({
  getHeaders, request, signal, refresh = refreshAdminAccessToken,
}: {
  getHeaders: () => Record<string, string>;
  request: (headers: Record<string, string>) => Promise<T>;
  signal: AbortSignal;
  refresh?: () => Promise<string | null>;
}): Promise<{ results: T | null; authenticationFailure: AuthenticationFailure | null }> {
  if (signal.aborted) return { results: null, authenticationFailure: null };
  const headers = getHeaders();
  if (!hasAdminAuthorizationHeader(headers)) {
    return { results: null, authenticationFailure: 'unauthenticated' };
  }

  let results = await request(headers);
  if (signal.aborted) return { results: null, authenticationFailure: null };
  let authenticationFailure = getAuthenticationFailure(results);
  if (authenticationFailure === 'unauthenticated') {
    const token = await refresh();
    if (signal.aborted) return { results: null, authenticationFailure: null };
    if (token) {
      results = await request({ ...getHeaders(), Authorization: `Bearer ${token}` });
      if (signal.aborted) return { results: null, authenticationFailure: null };
      authenticationFailure = getAuthenticationFailure(results);
    }
  }
  return { results, authenticationFailure };
}
